import { useAdvertisementComponent } from "@/hooks/useAdvertisementComponent";
import { Card } from "@/components/ui/card";
import { AlertTriangle } from "lucide-react";
import Markdown from "react-markdown";
import { AdvertisementSkeleton } from "./AdvertisementSkeleton";

interface AdvertisementComponentProps {
  isLoading?: boolean;
}

const AdvertisementComponent = ({ isLoading }: AdvertisementComponentProps) => {
  const { advertisement, advertisementColor } = useAdvertisementComponent();

  if (isLoading) {
    return <AdvertisementSkeleton />;
  }

  return (
    advertisement && (
      <Card
        className="prose prose-blue max-w-non mb-6 p-6"
        style={{ backgroundColor: advertisementColor }}
      >
        {advertisement.type === "warning" && (
          <div className="flex pb-2 text-amber-600">
            <AlertTriangle />
            <span className="px-2">Внимание!</span>
          </div>
        )}
        <Markdown
          components={{
            h1: ({ children }) => <h1 className="mb-4 text-2xl font-bold">{children}</h1>,
            h2: ({ children }) => <h2 className="mb-3 text-xl font-semibold">{children}</h2>,
            h3: ({ children }) => <h3 className="mb-2 text-lg font-medium">{children}</h3>,
            p: ({ children }) => (
              <p className="leading-relaxed text-muted-foreground">{children}</p>
            ),
            ul: ({ children }) => <ul className="list-disc space-y-2 pl-6">{children}</ul>,
            ol: ({ children }) => <ol className="list-decimal space-y-2 pl-6">{children}</ol>,
            li: ({ children }) => <li className="text-muted-foreground">{children}</li>,
            a: ({ children, href }) => (
              <a href={href} className="text-primary underline hover:text-primary/80">
                {children}
              </a>
            ),
            strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
            em: ({ children }) => <em className="italic">{children}</em>,
            blockquote: ({ children }) => (
              <blockquote className="my-4 border-l-4 border-primary/20 pl-4 italic text-muted-foreground">
                {children}
              </blockquote>
            ),
            code: ({ children }) => (
              <code className="rounded bg-muted px-1 py-0.5 text-sm">{children}</code>
            ),
          }}
        >
          {advertisement.content}
        </Markdown>
      </Card>
    )
  );
};

export default AdvertisementComponent;
